import type { SummaryContext, UniversalNodeSchema } from '../universal-node-schema'
import { UniversalNodeRegistry } from '../universal-node-schema'
import {
  Plus,
  Minus,
  X,
  Divide,
  Hash,
  Zap,
  Square,
  TrendingUp,
  Sigma,
  Calculator,
  Target,
  Percent,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Link2,
} from 'lucide-react'
import { averageEquation, formula, maxEquation, minEquation, sumEquation } from '../node-labels'

// Math Node Schemas
// These schemas define the complete UI and behavior for all math nodes

/**
 * The base's inputs, each saying which value it wants while unwired, where
 * the role matters ("Connect the whole"; Chris, 2026-10-06).
 */
const prompting = (texts: string[]) =>
  dualInputMathNodeBase.inputs.map((input, i) => ({ ...input, emptyText: texts[i] }))

// Dual Input Math Nodes (Add, Subtract, Multiply, Divide, Modulo, Power, Percentage, Percentage Change, Ratio)
const dualInputMathNodeBase: Omit<
  UniversalNodeSchema,
  'type' | 'displayName' | 'icon' | 'calculate'
> = {
  category: 'math',
  // Every input it needs is required: an unwired one reads the red "No input"
  // and the pre-run check names it (Chris, 2026-10-06).
  inputHandler: { type: 'dual' },
  inputs: [
    {
      id: 'input_1',
      displayName: 'A',

      acceptedTypes: ['number'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_2',
      displayName: 'B',

      acceptedTypes: ['number'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
      discriminatedUnion: {
        parameterName: 'inputBType',
        defaultStaticValue: 2,
        staticType: 'number',
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Result',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'inputBType',
      displayName: 'Input B Type',
      type: 'select',
      // The Condition's toggle, drawn as its two icons at the end of B's row
      // (node-face.ts inlineInputModes; Chris, 2026-10-06).
      display: 'segmented',
      default: 'node',
      options: [
        { value: 'node', label: 'Node', icon: Link2 },
        { value: 'static', label: 'Static', icon: Hash },
      ],
    },
  ],
  ui: {
    showEquation: true,
  },
}

// Single Input Math Nodes (Square Root, Absolute Value)
const singleInputMathNodeBase: Omit<
  UniversalNodeSchema,
  'type' | 'displayName' | 'icon' | 'calculate'
> = {
  category: 'math',
  // Every input it needs is required: an unwired one reads the red "No input"
  // and the pre-run check names it (Chris, 2026-10-06).
  inputHandler: { type: 'single' },
  inputs: [
    {
      id: 'input_1',
      displayName: 'A',

      acceptedTypes: ['number'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Result',
      type: 'number',
    },
  ],
  parameters: [],
  ui: {
    showEquation: true,
  },
}

// Dynamic Input Math Nodes (Sum, Average) - no parameters, inputs determined by connections
const dynamicInputMathNodeBase: Omit<
  UniversalNodeSchema,
  'type' | 'displayName' | 'icon' | 'calculate'
> = {
  category: 'math',
  inputs: [], // Will be generated dynamically based on connections
  outputs: [
    {
      id: 'value',
      displayName: 'Result',
      type: 'number',
    },
  ],
  parameters: [], // No manual input count parameter
  inputHandler: {
    type: 'multi',
    maxInputs: 8,
    minInputs: 2,
  },
  ui: {
    showEquation: true,
  },
}

// Specific Math Node Schemas
export const addNodeSchema: UniversalNodeSchema = {
  ...dualInputMathNodeBase,
  type: 'add',
  face: { shortName: 'Add', equation: formula('A + B') },
  displayName: 'Add',
  icon: Plus,
  calculate: (inputs: Record<string, number>) => ({ value: inputs.input_1 + inputs.input_2 }),
}

export const subtractNodeSchema: UniversalNodeSchema = {
  ...dualInputMathNodeBase,
  type: 'subtract',
  face: { shortName: 'Subtract', equation: formula('A - B') },
  displayName: 'Subtract',
  icon: Minus,
  calculate: (inputs: Record<string, number>) => ({ value: inputs.input_1 - inputs.input_2 }),
}

export const multiplyNodeSchema: UniversalNodeSchema = {
  ...dualInputMathNodeBase,
  type: 'multiply',
  face: { shortName: 'Multiply', equation: formula('A × B') },
  displayName: 'Multiply',
  icon: X,
  calculate: (inputs: Record<string, number>) => ({ value: inputs.input_1 * inputs.input_2 }),
}

export const divideNodeSchema: UniversalNodeSchema = {
  ...dualInputMathNodeBase,
  type: 'divide',
  face: { shortName: 'Divide', equation: formula('A ÷ B') },
  displayName: 'Divide',
  icon: Divide,
  calculate: (inputs: Record<string, number>) => ({
    value: inputs.input_2 !== 0 ? inputs.input_1 / inputs.input_2 : 0,
  }),
}

export const moduloNodeSchema: UniversalNodeSchema = {
  ...dualInputMathNodeBase,
  type: 'modulo',
  face: { equation: formula('A % B') },
  displayName: 'Modulo',
  icon: Hash,
  calculate: (inputs: Record<string, number>) => ({
    value: inputs.input_2 !== 0 ? inputs.input_1 % inputs.input_2 : 0,
  }),
}

export const powerNodeSchema: UniversalNodeSchema = {
  ...dualInputMathNodeBase,
  type: 'power',
  face: { equation: formula('A ^ B') },
  inputs: prompting(['Connect the base', 'Connect the exponent']),
  displayName: 'Power',
  icon: Zap,
  calculate: (inputs: Record<string, number>) => ({
    value: Math.pow(inputs.input_1, inputs.input_2),
  }),
}

export const squareRootNodeSchema: UniversalNodeSchema = {
  ...singleInputMathNodeBase,
  type: 'squareRoot',
  face: { equation: formula('√A') },
  displayName: 'Square Root',
  icon: Square,
  calculate: (inputs: Record<string, number>) => ({ value: Math.sqrt(Math.abs(inputs.input_1)) }),
}

export const absoluteValueNodeSchema: UniversalNodeSchema = {
  ...singleInputMathNodeBase,
  type: 'absoluteValue',
  face: { equation: formula('|A|') },
  displayName: 'Absolute Value',
  icon: Calculator,
  calculate: (inputs: Record<string, number>) => ({ value: Math.abs(inputs.input_1) }),
}

export const sumNodeSchema: UniversalNodeSchema = {
  ...dynamicInputMathNodeBase,
  type: 'sum',
  face: { shortName: 'Sum', equation: sumEquation },
  displayName: 'Sum',
  icon: Sigma,
  calculate: (inputs: Record<string, number>) => {
    const values = Object.values(inputs).filter((v) => !isNaN(v))
    return { value: values.reduce((sum, val) => sum + val, 0) }
  },
}

export const averageNodeSchema: UniversalNodeSchema = {
  ...dynamicInputMathNodeBase,
  type: 'average',
  face: { shortName: 'Average', equation: averageEquation },
  displayName: 'Average',
  icon: TrendingUp,
  calculate: (inputs: Record<string, number>) => {
    const values = Object.values(inputs).filter((v) => !isNaN(v))
    return {
      value: values.length > 0 ? values.reduce((sum, val) => sum + val, 0) / values.length : 0,
    }
  },
}

export const maxNodeSchema: UniversalNodeSchema = {
  ...dynamicInputMathNodeBase,
  type: 'max',
  face: { equation: maxEquation },
  displayName: 'Max',
  icon: ArrowUp,
  calculate: (inputs: Record<string, number>) => {
    const values = Object.values(inputs).filter((v) => !isNaN(v))
    return { value: values.length > 0 ? Math.max(...values) : 0 }
  },
}

export const minNodeSchema: UniversalNodeSchema = {
  ...dynamicInputMathNodeBase,
  type: 'min',
  face: { equation: minEquation },
  displayName: 'Min',
  icon: ArrowDown,
  calculate: (inputs: Record<string, number>) => {
    const values = Object.values(inputs).filter((v) => !isNaN(v))
    return { value: values.length > 0 ? Math.min(...values) : 0 }
  },
}

export const percentageNodeSchema: UniversalNodeSchema = {
  ...dualInputMathNodeBase,
  type: 'percentage',
  face: { shortName: 'Percentage', equation: formula('(A / B) × 100') },
  inputs: prompting(['Connect the part', 'Connect the whole']),
  displayName: 'Percentage',
  icon: Percent,
  calculate: (inputs: Record<string, number>) => ({
    value: inputs.input_2 !== 0 ? (inputs.input_1 / inputs.input_2) * 100 : 0,
  }),
}

export const percentageChangeNodeSchema: UniversalNodeSchema = {
  ...dualInputMathNodeBase,
  type: 'percentageChange',
  face: {
    shortName: 'PERCENTCHANGE',
    equation: formula('((B - A) / A) × 100'),
  },
  displayName: 'Percent Change',
  icon: ArrowUpDown,
  inputs: [
    {
      id: 'input_1',
      displayName: 'A',
      emptyText: 'Connect the old value',
      acceptedTypes: ['number'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_2',
      displayName: 'B',
      emptyText: 'Connect the new value',
      acceptedTypes: ['number'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
      discriminatedUnion: {
        parameterName: 'inputBType',
        staticType: 'number',
        defaultStaticValue: 1,
      },
    },
  ],
  calculate: (inputs: Record<string, number>) => ({
    value: inputs.input_1 !== 0 ? ((inputs.input_2 - inputs.input_1) / inputs.input_1) * 100 : 0,
  }),
}

export const ratioNodeSchema: UniversalNodeSchema = {
  ...dualInputMathNodeBase,
  type: 'ratio',
  face: { equation: formula('A : B') },
  displayName: 'Ratio',
  icon: Target,
  calculate: (inputs: Record<string, number>) => ({
    value: inputs.input_2 !== 0 ? inputs.input_1 / inputs.input_2 : 0,
  }),
}

/** "Close-5m between Low-1D and High-1D → 0 to 100%"; the input names stand in for unwired ones. */
const rangeReadout = (context?: Pick<SummaryContext, 'input' | 'staticValue'>): string => {
  const name = (handle: string, fallback: string) => {
    const typed = context?.staticValue(handle)
    if (typed !== undefined && typed !== null && typed !== '') return String(typed)
    const wired = context?.input(handle)
    return !wired || wired === handle || wired === 'No Input' || wired.length === 1
      ? fallback
      : wired
  }
  return `${name('input_1', 'Value')} between ${name('input_2', 'Range low')} and ${name('input_3', 'Range high')} → 0 to 100%`
}

export const customPositionInRangeNodeSchema: UniversalNodeSchema = {
  category: 'math',
  type: 'customPositionInRange',
  displayName: 'Position in Range (Custom)',
  description: 'Where the value sits in its range: 0 at the low, 100 at the high.',
  icon: Target,
  // The value, the low and the high: each one required.
  inputHandler: { type: 'all' },
  inputs: [
    {
      id: 'input_1',
      displayName: 'Value',
      emptyText: 'Connect the value',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_2',
      displayName: 'Range low',
      emptyText: 'Connect the range low',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_3',
      displayName: 'Range high',
      emptyText: 'Connect the range high',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Position',
      type: 'number',
    },
  ],
  parameters: [],
  ui: {
    showEquation: true,
  },
  // Where the value sits between the low and the high, read in plain words
  // with the wired sources named (Chris, 2026-10-06): "Min" and "Max" read
  // like functions, and the formula wrapped mid-expression.
  face: { headerColor: '#d946ef', equation: rangeReadout },
  calculate: (inputs: Record<string, number>) => {
    const { input_1: value, input_2: min, input_3: max } = inputs
    if (max === min) return { value: 0 }
    return { value: ((value - min) / (max - min)) * 100 }
  },
}

// NOTE: Switcher Node moved to logic-schemas.ts - no longer part of Math category

// Registration function
export function registerMathSchemas() {
  // console.log('Registering Math Node Schemas...');

  // Register all math node schemas
  UniversalNodeRegistry.register(addNodeSchema)
  UniversalNodeRegistry.register(subtractNodeSchema)
  UniversalNodeRegistry.register(multiplyNodeSchema)
  UniversalNodeRegistry.register(divideNodeSchema)
  UniversalNodeRegistry.register(moduloNodeSchema)
  UniversalNodeRegistry.register(powerNodeSchema)
  UniversalNodeRegistry.register(squareRootNodeSchema)
  UniversalNodeRegistry.register(absoluteValueNodeSchema)
  UniversalNodeRegistry.register(sumNodeSchema)
  UniversalNodeRegistry.register(averageNodeSchema)
  UniversalNodeRegistry.register(maxNodeSchema)
  UniversalNodeRegistry.register(minNodeSchema)
  UniversalNodeRegistry.register(percentageNodeSchema)
  UniversalNodeRegistry.register(percentageChangeNodeSchema)
  UniversalNodeRegistry.register(ratioNodeSchema)
  UniversalNodeRegistry.register(customPositionInRangeNodeSchema)

  // console.log('Registered 14 Math Node Schemas:',
  //   'Add, Subtract, Multiply, Divide, Modulo, Power, Square Root, Absolute Value,',
  //   'Sum, Average, Percentage, Percentage Change, Ratio, Position in Range'
  // );
}
